/**
 * COMPANY INFO STEP - ENHANCED
 * 
 * Features:
 * - Company Logo Upload (JPEG, PNG, PDF support)
 * - ZIP Code Auto-fill (City/State lookup)
 * - All company details
 * - Preview card
 */

import React, { useRef, useState, useCallback } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Building2, Upload, X, Image, FileText, MapPin, Loader2, Shield, FileCheck } from 'lucide-react';
import { STATE_TAX_DATA } from '../../utils/taxCalculations';
import { lookupZipCode, STATE_NAMES } from '../../utils/zipCodeLookup';
import FieldGate from '../FieldGate';
import AddressAutocomplete from '../AddressAutocomplete';

const US_STATES = Object.entries(STATE_TAX_DATA).map(([code, data]) => ({
  code,
  name: data.name
})).sort((a, b) => a.name.localeCompare(b.name));

export default function CompanyInfoStep({
  formData,
  updateFormData,
  missingFields = [],
  skippedFields = {},
  onToggleSkip,
  attemptedNext = false,
}) {
  const gateProps = (key) => ({
    fieldKey: key,
    missingFields,
    skippedFields,
    onToggleSkip,
    attemptedNext,
  });
  const fileInputRef = useRef(null);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [isLookingUpZip, setIsLookingUpZip] = useState(false);

  // ZIP Code auto-fill handler
  const handleZipCodeChange = useCallback(async (zipCode) => {
    updateFormData({ companyZip: zipCode });
    
    // Only lookup if ZIP is 5 digits
    if (zipCode && /^\d{5}$/.test(zipCode)) {
      setIsLookingUpZip(true);
      try {
        const result = await lookupZipCode(zipCode);
        if (result) {
          updateFormData({ 
            companyCity: result.city,
            companyState: result.stateCode
          });
          toast.success(`Auto-filled: ${result.city}, ${result.stateCode}`);
        }
      } catch (error) {
        console.error('ZIP lookup error:', error);
      } finally {
        setIsLookingUpZip(false);
      }
    }
  }, [updateFormData]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB for PDFs, 2MB for images)
    const maxSize = file.type === 'application/pdf' ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File must be less than ${file.type === 'application/pdf' ? '5MB' : '2MB'}`);
      return;
    }

    // Handle PDF files
    if (file.type === 'application/pdf') {
      setIsProcessingPdf(true);
      toast.info('Processing PDF logo...');
      
      // For PDF, we'll store the file and create a placeholder preview
      // The backend will handle PDF to image conversion during PDF generation
      const reader = new FileReader();
      reader.onload = (e) => {
        updateFormData({
          companyLogo: file,
          companyLogoPreview: null, // No preview for PDF
          companyLogoType: 'pdf',
          companyLogoBase64: e.target.result
        });
        setIsProcessingPdf(false);
        toast.success('PDF logo uploaded! It will appear on your paystub.');
      };
      reader.readAsDataURL(file);
      return;
    }

    // Handle image files (JPEG, PNG, etc.)
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image (PNG, JPG) or PDF file');
      return;
    }

    // Create preview for images
    const reader = new FileReader();
    reader.onload = (e) => {
      updateFormData({
        companyLogo: file,
        companyLogoPreview: e.target.result,
        companyLogoType: 'image',
        companyLogoBase64: e.target.result
      });
      toast.success('Logo uploaded successfully');
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    updateFormData({
      companyLogo: null,
      companyLogoPreview: null,
      companyLogoType: null,
      companyLogoBase64: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <CompanyPresetsPanel formData={formData} updateFormData={updateFormData} />

      {/* Logo Upload - Enhanced for JPEG + PDF */}
      <div className="space-y-2">
        <Label className="text-zinc-300">Company Logo (Optional)</Label>
        <div className="flex items-start gap-4">
          {formData.companyLogoPreview ? (
            <div className="relative">
              <img 
                src={formData.companyLogoPreview} 
                alt="Company Logo" 
                className="w-24 h-24 object-contain border border-zinc-700 rounded-lg bg-white"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute -top-2 -right-2 w-6 h-6"
                onClick={removeLogo}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : formData.companyLogoType === 'pdf' ? (
            <div className="relative">
              <div className="w-24 h-24 border-2 border-dashed border-green-500 rounded-lg flex flex-col items-center justify-center bg-green-500/10">
                <FileText className="w-8 h-8 text-green-400" />
                <span className="text-xs text-green-400 mt-1">PDF Logo</span>
              </div>
              <Button
                variant="destructive"
                size="icon"
                className="absolute -top-2 -right-2 w-6 h-6"
                onClick={removeLogo}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className={`w-24 h-24 border-2 border-dashed border-zinc-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-amber-500 transition-colors ${isProcessingPdf ? 'opacity-50' : ''}`}
            >
              {isProcessingPdf ? (
                <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
              ) : (
                <>
                  <Upload className="w-8 h-8 text-zinc-500" />
                  <span className="text-xs text-zinc-500 mt-1">Upload</span>
                </>
              )}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleLogoUpload}
            className="hidden"
            data-testid="logo-upload-input"
          />
          <div className="text-sm text-zinc-400">
            <p>Upload your company logo</p>
            <p className="text-xs text-zinc-500 mt-1">Supports: PNG, JPG, JPEG, PDF</p>
            <p className="text-xs text-zinc-500">Max size: 2MB (images) / 5MB (PDF)</p>
          </div>
        </div>
      </div>

      {/* Company Name */}
      <FieldGate {...gateProps('companyName')}>
        <Label className="text-zinc-300">Company Name (Line 1) *</Label>
        <Input
          value={formData.companyName || ''}
          onChange={(e) => updateFormData({ companyName: e.target.value.toUpperCase() })}
          placeholder="FIRST MERIDIAN CARE SERVICES INC."
          className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
          required
          data-testid="company-name-input"
        />
      </FieldGate>

      {/* Company Name Line 2 / DBA */}
      <div>
        <Label className="text-zinc-300">Company Name (Line 2) / DBA</Label>
        <Input
          value={formData.companyName2 || ''}
          onChange={(e) => updateFormData({ companyName2: e.target.value.toUpperCase() })}
          placeholder="DBA MERIDIAN HOME CARE (optional)"
          className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
          data-testid="company-name2-input"
        />
        <p className="text-xs text-zinc-500 mt-1">Use for DBA names or to split long company names across two lines</p>
      </div>

      {/* Phone Number */}
      <div>
        <Label className="text-zinc-300">Phone Number</Label>
        <Input
          value={formData.companyPhone || ''}
          onChange={(e) => updateFormData({ companyPhone: e.target.value })}
          placeholder="(555) 123-4567"
          className="mt-1 bg-zinc-800 border-zinc-600 text-white"
        />
      </div>

      {/* Street Address 1 */}
      <FieldGate {...gateProps('companyAddress1')}>
        <Label className="text-zinc-300">Street Address 1 *</Label>
        <AddressAutocomplete
          label={null}
          value={formData.companyAddress1 || ''}
          onChange={(e) => updateFormData({ companyAddress1: (typeof e === 'string' ? e : e.target.value).toUpperCase() })}
          onSelect={({ line, city, state, zip }) => {
            updateFormData({
              companyAddress1: (line || '').toUpperCase(),
              companyCity: (city || formData.companyCity || '').toUpperCase(),
              companyState: (state || formData.companyState || '').toUpperCase(),
              companyZip: zip || formData.companyZip || '',
            });
          }}
          placeholder="Start typing address…"
          testId="company-address1-input"
        />
      </FieldGate>

      {/* Street Address 2 */}
      <div>
        <Label className="text-zinc-300">Street Address 2</Label>
        <Input
          value={formData.companyAddress2 || ''}
          onChange={(e) => updateFormData({ companyAddress2: e.target.value.toUpperCase() })}
          placeholder="SUITE 100"
          className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* City */}
        <FieldGate {...gateProps('companyCity')}>
          <Label className="text-zinc-300">City *</Label>
          <Input
            value={formData.companyCity || ''}
            onChange={(e) => updateFormData({ companyCity: e.target.value.toUpperCase() })}
            placeholder="LOS ANGELES"
            className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
            required
            data-testid="company-city-input"
          />
        </FieldGate>

        {/* State */}
        <FieldGate {...gateProps('companyState')}>
          <Label className="text-zinc-300">State *</Label>
          <Select
            value={formData.companyState || 'CA'}
            onValueChange={(value) => updateFormData({ companyState: value })}
          >
            <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white" data-testid="company-state-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.code} value={state.code}>
                  {state.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldGate>

        {/* Zip Code - AUTO-FILL City/State */}
        <FieldGate {...gateProps('companyZip')}>
          <Label className="text-zinc-300 flex items-center gap-2">
            Zip Code *
            {isLookingUpZip && <Loader2 className="w-3 h-3 animate-spin text-amber-400" />}
          </Label>
          <div className="relative">
            <Input
              value={formData.companyZip || ''}
              onChange={(e) => {
                const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
                handleZipCodeChange(zip);
              }}
              placeholder="ENTER ZIP TO AUTO-FILL"
              className="mt-1 bg-zinc-800 border-amber-500/50 text-white"
              maxLength={5}
              required
              data-testid="company-zip-input"
            />
          </div>
          <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            Auto-fills City & State
          </p>
        </FieldGate>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* OPTIONAL VERIFICATION FEATURES */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="border-t border-zinc-700 pt-6 mt-6">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-400" />
          Optional Verification Features
        </h4>
        
        {/* EIN Toggle + Input */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.showEIN || false}
                onCheckedChange={(checked) => updateFormData({ showEIN: checked })}
                data-testid="show-ein-toggle"
              />
              <Label className="text-zinc-300 cursor-pointer">Show EIN</Label>
            </div>
          </div>
          
          {formData.showEIN && (
            <div className="ml-12">
              <Input
                value={formData.companyEIN || ''}
                onChange={(e) => {
                  // Format as XX-XXXXXXX
                  let val = e.target.value.replace(/\D/g, '').slice(0, 9);
                  if (val.length > 2) {
                    val = val.slice(0, 2) + '-' + val.slice(2);
                  }
                  updateFormData({ companyEIN: val });
                }}
                placeholder="XX-XXXXXXX"
                className="bg-zinc-800 border-zinc-600 text-white"
                maxLength={10}
                data-testid="company-ein-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Employer Identification Number</p>
            </div>
          )}
          {!formData.showEIN && (
            <p className="text-xs text-zinc-500 ml-12">Add EIN to paystub for verification</p>
          )}
        </div>
        
        {/* ADP Payroll Provider Toggle */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.showPayrollProvider || false}
              onCheckedChange={(checked) => updateFormData({ showPayrollProvider: checked })}
              data-testid="show-payroll-provider-toggle"
            />
            <Label className="text-zinc-300 cursor-pointer">ADP Footer</Label>
          </div>
        </div>
        <p className="text-xs text-zinc-500 ml-12 mt-1">
          {formData.showPayrollProvider 
            ? 'Will show "Processed by ADP" footer' 
            : 'Add ADP payroll provider footer'
          }
        </p>
      </div>

      {/* Preview Card */}
      <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
        <h4 className="text-sm font-semibold text-zinc-400 mb-3 flex items-center gap-2">
          <FileCheck className="w-4 h-4" />
          Preview
        </h4>
        <div className="flex items-start gap-3">
          {formData.companyLogoPreview && (
            <img src={formData.companyLogoPreview} alt="Logo" className="w-12 h-12 object-contain bg-white rounded" />
          )}
          {formData.companyLogoType === 'pdf' && !formData.companyLogoPreview && (
            <div className="w-12 h-12 bg-green-500/20 rounded flex items-center justify-center">
              <FileText className="w-6 h-6 text-green-400" />
            </div>
          )}
          <div className="flex-1">
            <p className="font-bold text-white">{formData.companyName || 'Company Name'}</p>
            <p className="text-sm text-zinc-400">
              {formData.companyAddress1 || '123 Main Street'}
              {formData.companyAddress2 && `, ${formData.companyAddress2}`}
            </p>
            <p className="text-sm text-zinc-400">
              {formData.companyCity || 'City'}, {formData.companyState || 'CA'} {formData.companyZip || '00000'}
            </p>
            {formData.companyPhone && (
              <p className="text-sm text-zinc-400 mt-1">
                {formData.companyPhone}
              </p>
            )}
            {formData.showEIN && formData.companyEIN && (
              <p className="text-sm text-amber-400 mt-1">
                EIN: {formData.companyEIN}
              </p>
            )}
          </div>
        </div>
        {formData.showPayrollProvider && (
          <div className="mt-3 pt-3 border-t border-zinc-700 text-xs text-zinc-500 text-center">
            Processed by ADP | This is a computer-generated document
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Company Presets Panel — save + load company profiles (name, address,
// phone, logo).  Data persisted to localStorage under key `company_presets`.
// Includes one baked-in preset ("SRS World Corp") that ships with the app.
// ═══════════════════════════════════════════════════════════════════

const BUILTIN_PRESETS = [
  {
    id: 'srs-world-corp',
    isBuiltIn: true,
    name: 'SRS WORLD CORP',
    companyName: 'SRS WORLD CORP',
    companyName2: '',
    companyAddress1: '750 OTAY LAKES RD #1035',
    companyAddress2: '',
    companyCity: 'CHULA VISTA',
    companyState: 'CA',
    companyZip: '91910',
    companyPhone: '',
    companyEIN: '87-1350446',
    employeeIdPrefix: 'SRS-',
    companyLogoPreview: '/preset-logos/srs-world-corp-a.png',
    companyLogoType: 'image',
  },
];

const LS_KEY = 'company_presets_v1';

function readPresets() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writePresets(list) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch { /* storage unavailable */ }
}

function CompanyPresetsPanel({ formData, updateFormData }) {
  const [userPresets, setUserPresets] = React.useState(readPresets);
  const [showSaveInput, setShowSaveInput] = React.useState(false);
  const [presetName, setPresetName] = React.useState('');

  const allPresets = [...BUILTIN_PRESETS, ...userPresets];

  const applyPreset = async (p) => {
    // For built-in logos served from /public, fetch and convert to data URL so
    // it embeds directly in the paystub PDF pipeline (same as user uploads).
    let logoPreview = p.companyLogoPreview || '';
    if (logoPreview && logoPreview.startsWith('/')) {
      try {
        const res = await fetch(logoPreview);
        const blob = await res.blob();
        logoPreview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        // Keep the URL fallback — the img tag will still render.
      }
    }
    updateFormData({
      companyName: p.companyName || '',
      companyName2: p.companyName2 || '',
      companyAddress1: p.companyAddress1 || '',
      companyAddress2: p.companyAddress2 || '',
      companyCity: p.companyCity || '',
      companyState: p.companyState || '',
      companyZip: p.companyZip || '',
      companyPhone: p.companyPhone || '',
      companyEIN: p.companyEIN || '',
      companyLogoPreview: logoPreview,
      companyLogoType: p.companyLogoType || 'image',
      // If the preset ships with an employee-ID prefix (e.g. "SRS-"),
      // seed the field only when it's empty OR already carries that
      // prefix — so we never trample a user-entered ID.
      ...(p.employeeIdPrefix
        ? (() => {
            const cur = (formData.employeeId || '').trim();
            const shouldSeed = !cur || cur.toUpperCase().startsWith(p.employeeIdPrefix.toUpperCase());
            return shouldSeed ? { employeeId: p.employeeIdPrefix } : {};
          })()
        : {}),
    });
    toast.success(`Loaded preset: ${p.name}`);
  };

  const saveCurrentAsPreset = () => {
    const label = (presetName || formData.companyName || '').trim();
    if (!label) {
      toast.error('Enter a preset name or fill in Company Name first');
      return;
    }
    const rec = {
      id: `p_${Date.now()}`,
      isBuiltIn: false,
      name: label.toUpperCase(),
      companyName: formData.companyName || '',
      companyName2: formData.companyName2 || '',
      companyAddress1: formData.companyAddress1 || '',
      companyAddress2: formData.companyAddress2 || '',
      companyCity: formData.companyCity || '',
      companyState: formData.companyState || '',
      companyZip: formData.companyZip || '',
      companyPhone: formData.companyPhone || '',
      companyEIN: formData.companyEIN || '',
      companyLogoPreview: formData.companyLogoPreview || '',
      companyLogoType: formData.companyLogoType || 'image',
    };
    const next = [...userPresets, rec];
    setUserPresets(next); writePresets(next);
    setShowSaveInput(false); setPresetName('');
    toast.success(`Saved preset: ${rec.name}`);
  };

  const deletePreset = (id) => {
    if (!window.confirm('Delete this saved preset?')) return;
    const next = userPresets.filter((p) => p.id !== id);
    setUserPresets(next); writePresets(next);
    toast.success('Preset deleted');
  };

  return (
    <div className="rounded-lg border-2 border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-500/10 to-purple-500/5 p-4"
      data-testid="company-presets-panel">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-fuchsia-300" />
          <h3 className="text-sm font-bold text-white">Company Presets</h3>
          <span className="text-[10px] uppercase tracking-wider text-fuchsia-200 bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-full px-2 py-0.5">
            Save time
          </span>
        </div>
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => setShowSaveInput((s) => !s)}
          className="text-xs border-fuchsia-500/50 hover:bg-fuchsia-500/20"
          data-testid="preset-save-toggle"
        >
          {showSaveInput ? 'Cancel' : '+ Save Current as Preset'}
        </Button>
      </div>

      {showSaveInput && (
        <div className="flex gap-2 mb-3">
          <Input
            value={presetName} onChange={(e) => setPresetName(e.target.value)}
            placeholder={formData.companyName ? `Preset name (defaults to "${formData.companyName}")` : 'Enter a preset name'}
            className="flex-1 bg-zinc-900 border-zinc-700 text-white text-sm"
            data-testid="preset-name-input"
          />
          <Button type="button" onClick={saveCurrentAsPreset}
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs"
            data-testid="preset-save-btn">
            Save Preset
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {allPresets.map((p) => (
          <div key={p.id}
            className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-2.5 hover:border-fuchsia-500/60 transition"
            data-testid={`preset-card-${p.id}`}>
            <div className="flex items-center gap-2">
              {p.companyLogoPreview && (
                <img src={p.companyLogoPreview} alt=""
                  className="w-10 h-10 object-contain rounded bg-white p-0.5 border border-zinc-700" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-white truncate">{p.name}</div>
                <div className="text-[10px] text-zinc-400 truncate">
                  {p.companyAddress1 || '—'}
                  {p.companyCity && ` · ${p.companyCity}, ${p.companyState || ''}`}
                </div>
                {p.isBuiltIn && (
                  <span className="text-[9px] uppercase tracking-wider text-emerald-300 bg-emerald-500/20 rounded px-1 mt-0.5 inline-block">
                    Built-in
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-1 mt-2">
              <Button type="button" size="sm" onClick={() => applyPreset(p)}
                className="flex-1 h-7 text-[11px] bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
                data-testid={`preset-apply-${p.id}`}>
                Use this preset
              </Button>
              {!p.isBuiltIn && (
                <Button type="button" size="sm" variant="outline"
                  onClick={() => deletePreset(p.id)}
                  className="h-7 px-2 text-[11px] text-red-300 border-red-500/40 hover:bg-red-500/20"
                  data-testid={`preset-delete-${p.id}`}>
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-500 mt-2">
        Presets are saved on this device. The built-in preset ships with the app for quick access to your primary company info.
      </p>
    </div>
  );
}
